import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStationwiseTotals from "../hooks/useStationWiseTotal";
import { ChevronDown, ChevronUp } from "lucide-react";

const StationTotalsTable = ({ customRange = null }) => {
  const navigate = useNavigate();
  const { stationData, isLoading, error, refetch } = useStationwiseTotals(customRange);
  const [cachedData, setCachedData] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    refetch();
    const localCache = JSON.parse(localStorage.getItem("stations_cache_1") || "[]");
    setCachedData(localCache);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customRange]);

  const getStationName = (stationId) => {
    const station = cachedData.find((s) => s.stationId === stationId);
    return station ? station.stationName : stationId;
  };

  // merge cached stations with API data
  const mergedData = cachedData.map((cacheStation) => {
    const apiStation = stationData?.find((s) => s.StationID === cacheStation.stationId);
    return apiStation
      ? apiStation
      : {
          StationID: cacheStation.stationId,
          TotalSessions: 0,
          TotalGSTAmount: 0,
          TotalCost: 0,
          TotalFinalCost: 0,
          TotalEnergyKWh: 0,
          TotalDurationSeconds: 0,
        };
  });

  if (isLoading) {
    return <p className="text-13 text-neutral-500 text-center py-8">Loading station totals...</p>;
  }

  if (!mergedData || mergedData.length === 0) {
    return <p className="text-13 text-neutral-500 text-center py-8">No data available</p>;
  }

  // handle collapse (max 3 by default)
  const visibleData = expanded ? mergedData : mergedData.slice(0, 3);

  return (
    <div className="mt-6 overflow-x-auto rounded-xl border border-neutral-200 shadow-card">
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">
              Station Name
            </th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">
              Sessions
            </th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">
              GST Amount
            </th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">
              Total Cost
            </th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">
              Final Cost
            </th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">
              Energy (kWh)
            </th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">
              Duration (s)
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-100">
          {visibleData.map((station) => (
            <tr
              key={station.StationID}
              className="hover:bg-neutral-50 cursor-pointer transition-colors duration-150"
              onClick={() => navigate(`/charging-stations/${station.StationID}`)}
            >
            <td className="px-5 py-3 text-13 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-brand-950">{getStationName(station.StationID)}</span>
                <span className="rounded-md bg-neutral-50 px-2 py-0.5 font-mono text-[11px] font-medium text-neutral-600 ring-1 ring-inset ring-neutral-200">
                  {station.StationID}
                </span>
              </div>
            </td>            
              <td className="px-5 py-3 text-13 tabular-nums text-neutral-700">{station.TotalSessions}</td>
              <td className="px-5 py-3 text-13 tabular-nums text-neutral-700">{station.TotalGSTAmount}</td>
              <td className="px-5 py-3 text-13 tabular-nums text-neutral-700">{station.TotalCost}</td>
              <td className="px-5 py-3 text-13 tabular-nums text-neutral-700">{station.TotalFinalCost}</td>
              <td className="px-5 py-3 text-13 tabular-nums text-neutral-700">{station.TotalEnergyKWh}</td>
              <td className="px-5 py-3 text-13 tabular-nums text-neutral-700">{station.TotalDurationSeconds}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {mergedData.length > 3 && (
        <div className="flex justify-center border-t border-neutral-100 py-2.5">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center text-13 font-medium text-neutral-600 hover:text-brand-700"
          >
            {expanded ? (
              <>
                Show Less <ChevronUp className="ml-1 w-4 h-4" />
              </>
            ) : (
              <>
                Show More <ChevronDown className="ml-1 w-4 h-4" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default StationTotalsTable;
